import { useEffect, useState } from "react";
import { Steps, message, Row, Col, Image } from "antd";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { FormIcon } from "../../../Icons/FormIcon";
import { Header } from "../../../components/Header";
import "./style.scss";
import { BackArrow } from "../../../Icons/BackArrow";
import { useNavigate, useParams } from "react-router-dom";
import { useDeleteData } from "../../../Network/useDeleteData";
import { DeleteConfirmation } from "./DeleteModal";
import { useGetData } from "../../../Network/useGetQueryData";
import { TypeSteps } from "./TypeStep";
import { ServiceStep } from "./Service";
import { ProviderStep } from "./Provider";
import { SummaryStep } from "./Summary";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { UserRolesId } from "../../enum";

const { Step } = Steps;

const AddRequestSteps = () => {
  const { t, i18n } = useTranslation();
  const stepNum = useQueryParams("step");
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(+stepNum! || 0);
  const [isDeleteView, setIsDeleteView] = useState<boolean>();
  const [formData, setFormData] = useState<any>({});
  const userInfo = useSelector((state: any) => state.user.user) || {};

  useEffect(() => {
    return () => {
      setFormData({});
    };
  }, []);

  const stepsConfig = [
    { title: t("addRequest.type"), component: TypeSteps },
    { title: t("addRequest.service"), component: ServiceStep },
    ...(userInfo?.roleId === UserRolesId.Provider
      ? []
      : [{ title: t("addRequest.provider"), component: ProviderStep }]),
    { title: t("addRequest.summary"), component: SummaryStep },
  ];

  const CurrentStepComponent = stepsConfig[currentStep].component;

  const { data: requestInfo } = useGetData<{
    data: any;
  }>({
    queryKey: ["service-request-details"],
    url: `service-request/${requestId}`,
    options: {
      enabled: !!requestId,
      queryKey: ["service-request-details"],
    },
  });

  useEffect(() => {
    if (requestInfo?.data) {
      const data = requestInfo.data;
      const allServicesDetailed: any[] = []; // for 'summary'
      const allServiceIds: number[] = []; // for 'servicesIds'
      const servicesForFormData: { serviceId: number; name: string }[] = []; // for 'services'

      let primaryServiceProviderId: number | undefined;

      if (data.children && data.children.length > 0) {
        primaryServiceProviderId = data.children[0].serviceProvider?.id;

        data.children.forEach((child: any) => {
          child.services.forEach((s: any) => {
            if (s.service) {
              allServicesDetailed.push(s.service);
              allServiceIds.push(s.service.id);
              servicesForFormData.push({
                serviceId: s.service.id,
                name:
                  s.service.name?.[i18n.language || "en"] ||
                  s.service.name?.en ||
                  s.service.name?.ar,
              });
            }
          });
        });
      }

      setFormData({
        ...requestInfo?.data,
        servicesIds: allServiceIds,
        services: servicesForFormData,
        summary: allServicesDetailed,
        providers: primaryServiceProviderId,
        serviceProviderId: primaryServiceProviderId
          ? { providers: primaryServiceProviderId }
          : undefined,
      });
    } else {
      setFormData({});
    }
  }, [requestInfo]);

  const { mutate: deleteItem } = useDeleteData({
    url: `service-provider/${requestId}`,
    options: {
      onSuccess: () => {
        message.success(t("addRequest.deleteSuccess"));
        navigate("/providers");
      },
      onError: (error) => {
        message.error(t("addRequest.deleteError"));
        console.error("Failed to delete item:", error);
      },
    },
  });

  const handleStepClick = async (step: number) => {
    if (step >= currentStep) return;
    if (currentStep === 0 && !requestId) return;
    setCurrentStep(step);
  };

  const handleDeleteProvider = () => {
    try {
      deleteItem();
    } catch (error) {}
  };

  return (
    <div>
      <Header
        title={t("addRequest.requests")}
        backIcon={
          <span
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/requests")}
          >
            <BackArrow />
          </span>
        }
      />
      <div className={`add-provider-header ${requestId ? "edit-view" : ""}`}>
        <h2>
          {requestId ? t("addRequest.edit") : t("addRequest.add")}{" "}
          {t("addRequest.request")}
        </h2>
        <Steps
          labelPlacement="vertical"
          current={currentStep}
          onChange={handleStepClick}
        >
          {stepsConfig.map((item, index) => (
            <Step key={index} title={item.title} />
          ))}
        </Steps>
      </div>
      <div className="steps-container">
        <Row gutter={30}>
          <Col lg={24} xl={16}>
            <div className="step-wrapper-component">
              <CurrentStepComponent
                setFormData={setFormData}
                onChangeStep={setCurrentStep}
                formData={formData}
              />
            </div>
          </Col>
          <Col lg={24} xl={8}>
            {requestInfo?.data?.prescriptionUrl ? (
              <Image width={200} src={requestInfo?.data?.prescriptionUrl} />
            ) : (
              <div className="form-icon-text">
                <FormIcon />
                <p>{t("addRequest.fillDetailsNote")}</p>
              </div>
            )}
          </Col>
        </Row>

        {isDeleteView && (
          <DeleteConfirmation
            onCloseModal={() => setIsDeleteView(false)}
            provider={requestInfo?.data?.prescriptionUrl?.data}
            onDelete={handleDeleteProvider}
          />
        )}
      </div>
    </div>
  );
};

export default AddRequestSteps;
